import { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase, getAuthHistory, commitAuthChange } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { format, differenceInDays } from 'date-fns';
import { parseLocalDate } from '../../lib/dateUtils';
import AuthHistoryTable from '../../components/AuthHistoryTable';
import { StatTile } from '../../components/StatTile';
import DataTable from '../../components/DataTable';
import { CLOSED_STATUS_FILTER, isDmeOnly } from '../../lib/serviceModel';
import ClientTrail from '../../components/ClientTrail';

const URGENCY_FILTERS = ['All', 'Expiring ≤30d', 'Expiring ≤60d', 'Expired', 'Missing'];
// Home-health authorizations only. DME is authorized per order by CareSource
// and lives on the order — see the DME Prior Auth page (/dme/auth). This page
// used to keep a separate "DME Auth" on the client record, which nothing in
// the DME workflow read (removed 2026-10-19; no client had one).
const TYPE_FILTERS    = ['All Types', 'DMA 6A', 'PA'];
const AUTH_TYPES      = ['DMA 6A', 'PA'];

const EMPTY_FORM = {
  clientId: '', authType: 'PA', expiryDate: '', authNumber: '',
  skilledHoursWeek: '', pssHoursWeek: '', notes: '', status: 'active',
};

export default function PriorAuth() {
  const navigate   = useNavigate();
  const { profile } = useAuth();

  const [clients,    setClients]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [filter,     setFilter]     = useState('All');
  const [typeFilter, setTypeFilter] = useState('All Types');
  const [search,     setSearch]     = useState('');
  // Opened from a client (?client=<id>): narrowed to that client, with the way
  // back to them. "All clients" clears it.
  const [sp, setSp] = useSearchParams();
  const fromClient = sp.get('client');
  useEffect(() => {
    if (!fromClient) return;
    supabase.from('clients').select('client_id').eq('id', fromClient).maybeSingle().then(({ data }) => { if (data?.client_id) setSearch(data.client_id); });
  }, [fromClient]);
  const [sortKey,    setSortKey]    = useState('expiry');
  const [sortDir,    setSortDir]    = useState('asc');

  // Modal states
  const [showModal,  setShowModal]  = useState(false);
  const [editingRec, setEditingRec] = useState(null); // null = add, object = edit
  const [form,       setForm]       = useState(EMPTY_FORM);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState('');
  const [success,    setSuccess]    = useState('');

  // History modal
  const [historyRec,  setHistoryRec]  = useState(null); // the record we're viewing history for
  const [historyRows, setHistoryRows] = useState(null); // null = loading
  const openHistory = async (rec) => {
    setHistoryRec(rec);
    setHistoryRows(null);
    const { data } = await getAuthHistory(rec.id, rec.authType !== '—' ? rec.authType : null);
    setHistoryRows(data || []);
  };

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('clients')
      .select('id, client_id, first_name, last_name, current_status, pa_expiry_date, pa_renewal_date, dma_6a_expiry_date, dma_6a_number, dma_6a_submission_date, dma_80_pa_number, dma_80_submission_date, pa_skilled_hours_week, pa_pss_hours_week, responsible_person_name, medicaid_id, service_lines')
      .not('current_status', 'in', CLOSED_STATUS_FILTER)
      .order('last_name');
    setClients(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const today = new Date();

  const getUrgency = (expiry) => {
    if (!expiry) return null;
    const days = differenceInDays(parseLocalDate(expiry), today);
    if (days < 0)    return { label: 'Expired',       color: 'var(--danger)',  bg: 'var(--danger-bg)',  days, priority: 0 };
    if (days < 60)   return { label: `${days}d left`, color: 'var(--danger)',  bg: 'var(--danger-bg)',  days, priority: 1 };
    if (days <= 90)  return { label: `${days}d left`, color: 'var(--warn)',    bg: 'var(--warn-bg)',    days, priority: 2 };
    return                  { label: `${days}d left`, color: 'var(--success)', bg: 'var(--success-bg)', days, priority: 3 };
  };

  // Build flat auth records
  const authRecords = useMemo(() => clients.filter(c => !isDmeOnly(c)).flatMap(c => {
    const records = [];
    if (c.dma_6a_expiry_date)   records.push({ ...c, authType: 'DMA 6A',   expiry: c.dma_6a_expiry_date,   authNum: c.dma_80_pa_number, serviceLine: 'home_health', field: 'dma_6a_expiry_date' });
    if (c.pa_expiry_date)       records.push({ ...c, authType: 'PA',       expiry: c.pa_expiry_date,       authNum: c.dma_80_pa_number, serviceLine: 'home_health', field: 'pa_expiry_date' });
    if (!c.dma_6a_expiry_date && !c.pa_expiry_date)
      records.push({ ...c, authType: '—', expiry: null, authNum: null, serviceLine: 'home_health', field: null });
    return records;
  }), [clients]);

  // Filter
  const filtered = useMemo(() => authRecords.filter(r => {
    const u = getUrgency(r.expiry);
    const urgencyMatch =
      filter === 'All'            ? true :
      filter === 'Expiring ≤30d'  ? (u && u.days >= 0 && u.days <= 30) :
      filter === 'Expiring ≤60d'  ? (u && u.days >= 0 && u.days < 60) :
      filter === 'Expired'        ? (u && u.days < 0) :
      !r.expiry;
    const typeMatch =
      typeFilter === 'All Types' ? true :
      r.authType === typeFilter;
    const searchMatch = !search ||
      `${r.first_name} ${r.last_name}`.toLowerCase().includes(search.toLowerCase()) ||
      r.client_id?.toLowerCase().includes(search.toLowerCase()) ||
      r.medicaid_id?.toLowerCase().includes(search.toLowerCase());
    return urgencyMatch && typeMatch && searchMatch;
  }), [authRecords, filter, typeFilter, search]);

  // Sort
  const sorted = useMemo(() => [...filtered].sort((a, b) => {
    let av, bv;
    switch (sortKey) {
      case 'name':        av = `${a.last_name} ${a.first_name}`; bv = `${b.last_name} ${b.first_name}`; break;
      case 'client_id':   av = a.client_id; bv = b.client_id; break;
      case 'authType':    av = a.authType;  bv = b.authType;  break;
      case 'responsible': av = a.responsible_person_name || ''; bv = b.responsible_person_name || ''; break;
      case 'status':      av = a.current_status; bv = b.current_status; break;
      case 'expiry':
        if (!a.expiry && !b.expiry) return 0;
        if (!a.expiry) return 1;
        if (!b.expiry) return -1;
        av = new Date(a.expiry); bv = new Date(b.expiry);
        return sortDir === 'asc' ? av - bv : bv - av;
      case 'urgency': {
        const ua = getUrgency(a.expiry); const ub = getUrgency(b.expiry);
        const pa = ua?.priority ?? 99;   const pb = ub?.priority ?? 99;
        if (pa !== pb) return sortDir === 'asc' ? pa - pb : pb - pa;
        // Same priority bucket → sort by days so fewest days rises to top
        const da = ua?.days ?? 9999;     const db = ub?.days ?? 9999;
        return sortDir === 'asc' ? da - db : db - da;
      }
      default: return 0;
    }
    const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
    return sortDir === 'asc' ? cmp : -cmp;
  }), [filtered, sortKey, sortDir]);

  const sorting = useMemo(() => sortKey ? [{ id: sortKey, desc: sortDir === 'desc' }] : [], [sortKey, sortDir]);
  const handleSortingChange = (updaterOrValue) => {
    const next = typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;
    if (!next.length) return;
    setSortKey(next[0].id);
    setSortDir(next[0].desc ? 'desc' : 'asc');
  };

  const stats = useMemo(() => ({
    total:    authRecords.filter(r => r.expiry).length,
    expired:  authRecords.filter(r => { const u = getUrgency(r.expiry); return u && u.days < 0; }).length,
    expiring: authRecords.filter(r => { const u = getUrgency(r.expiry); return u && u.days >= 0 && u.days < 60; }).length,
    missing:  clients.filter(c => !isDmeOnly(c) && !c.pa_expiry_date && !c.dma_6a_expiry_date).length,
  }), [authRecords, clients]);

  // ── Add / Edit auth ────────────────────────────────────────────────────────
  const openAdd = (rec = null) => {
    setEditingRec(rec);
    setForm(rec ? {
      clientId: rec.id,
      authType: rec.authType === '—' ? 'PA' : rec.authType,
      expiryDate: rec.expiry || '',
      authNumber: rec.dma_80_pa_number || '',
      skilledHoursWeek: rec.pa_skilled_hours_week != null ? String(rec.pa_skilled_hours_week) : '',
      pssHoursWeek:     rec.pa_pss_hours_week     != null ? String(rec.pa_pss_hours_week)     : '',
      notes: '',
      status: 'active',
    } : EMPTY_FORM);
    setError('');
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.clientId && !editingRec) { setError('Please select a patient'); return; }
    if (!form.expiryDate) { setError('Expiry date is required'); return; }
    setSaving(true);
    setError('');

    const clientId  = editingRec?.id || form.clientId;
    const srcRec    = editingRec || clients.find(c => c.id === form.clientId) || {};
    const clientRef = srcRec.client_id || null;
    const num = (v) => (v !== '' && v != null && !Number.isNaN(parseFloat(v)) ? parseFloat(v) : null);

    // Build the current-cache patch + the history snapshot, per auth type.
    const updatePayload = {};
    const record = { auth_number: null, submitted_date: null, expiry_date: form.expiryDate || null, renewal_date: null };

    if (form.authType === 'DMA 6A') {
      updatePayload.dma_6a_expiry_date = form.expiryDate;
      record.auth_number    = srcRec.dma_6a_number || null;
      record.submitted_date = srcRec.dma_6a_submission_date || null;
    } else if (form.authType === 'PA') {
      updatePayload.pa_expiry_date = form.expiryDate;
      if (form.authNumber) updatePayload.dma_80_pa_number = form.authNumber;
      updatePayload.pa_skilled_hours_week = num(form.skilledHoursWeek);
      updatePayload.pa_pss_hours_week     = num(form.pssHoursWeek);
      record.auth_number        = form.authNumber || srcRec.dma_80_pa_number || null;
      record.submitted_date     = srcRec.dma_80_submission_date || null;
      record.renewal_date       = srcRec.pa_renewal_date || null;
      record.skilled_hours_week = num(form.skilledHoursWeek);
      record.pss_hours_week     = num(form.pssHoursWeek);
    }
    if (form.notes) record.notes = form.notes;

    const { error: err } = await commitAuthChange({
      clientId, clientRef, authType: form.authType, record, currentColumns: updatePayload, profile,
    });
    if (err) { setError(err.message); setSaving(false); return; }

    // Log activity
    await supabase.from('activity_log').insert([{
      client_id: clientId,
      activity_type: editingRec ? 'Auth Updated' : 'Auth Added',
      notes: `${form.authType} ${editingRec ? 'updated' : 'added'} — expires ${format(parseLocalDate(form.expiryDate), 'MMM d, yyyy')}${form.notes ? `. ${form.notes}` : ''}`,
      performed_by: profile?.id,
      performed_by_name: profile?.full_name,
    }]);

    await load();
    setShowModal(false);
    setSuccess(`${form.authType} ${editingRec ? 'updated' : 'added'} successfully`);
    setTimeout(() => setSuccess(''), 3000);
    setSaving(false);
  };

  // ── Renewal workflow ───────────────────────────────────────────────────────
  const handleRenewal = async (rec) => {
    if (!window.confirm(`Start renewal for ${rec.first_name} ${rec.last_name} — ${rec.authType}?\n\nThis will create a task and log the action.`)) return;

    // Create a task
    await supabase.from('tasks').insert([{
      title: `Renew ${rec.authType} — ${rec.first_name} ${rec.last_name}`,
      client_ref: rec.client_id,
      client_id: rec.id,
      due_date: format(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'), // 14 days
      priority: rec.expiry && differenceInDays(parseLocalDate(rec.expiry), today) < 60 ? 'Urgent' : 'Normal',
      status: 'Open',
      assigned_to_id: profile?.id,
      assigned_to_name: profile?.full_name,
      created_by_id: profile?.id,
      created_by_name: profile?.full_name,
      notes: `${rec.authType} expires ${rec.expiry ? format(parseLocalDate(rec.expiry), 'MMM d, yyyy') : 'unknown'}. Initiate renewal process.`,
    }]);

    // Log activity
    await supabase.from('activity_log').insert([{
      client_id: rec.id,
      activity_type: 'Renewal Started',
      notes: `${rec.authType} renewal initiated by ${profile?.full_name}`,
      performed_by: profile?.id,
      performed_by_name: profile?.full_name,
    }]);

    setSuccess(`Renewal task created for ${rec.first_name} ${rec.last_name}`);
    setTimeout(() => setSuccess(''), 3000);
  };

  // ── CSV Export ─────────────────────────────────────────────────────────────
  const exportCSV = () => {
    const headers = ['Patient', 'ID', 'Medicaid ID', 'Auth Type', 'Expiry Date', 'Days Left', 'Status', 'Responsible'];
    const rows = sorted.map(r => {
      const u = getUrgency(r.expiry);
      return [
        `${r.first_name} ${r.last_name}`,
        r.client_id,
        r.medicaid_id || '',
        r.authType,
        r.expiry ? format(parseLocalDate(r.expiry), 'MM/dd/yyyy') : '',
        u ? (u.days < 0 ? 'Expired' : `${u.days} days`) : 'No auth',
        r.current_status,
        r.responsible_person_name || '',
      ];
    });
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `prior-auth-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const pill = (label, color, bg) => (
    <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, fontWeight: 500, background: bg, color }}>{label}</span>
  );

  const btnPill = { padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', border: '0.5px solid', transition: 'all 0.12s' };

  // enrich with derived per-row fields once, referenced by multiple columns below
  const rows = sorted.map(r => {
    const urgency = getUrgency(r.expiry);
    return { ...r, _urgency: urgency, _isActionable: !!(r.expiry && urgency && urgency.days <= 60) };
  });

  const columns = [
    {
      id: 'name', header: 'Patient', size: 170,
      meta: { mobile: 'primary' },
      cell: info => {
        const r = info.row.original;
        return (
          <span style={{ fontWeight: 500, color: 'var(--accent2)', cursor: 'pointer' }} onClick={() => navigate(`/clients/${r.id}`)}>
            {r.last_name}, {r.first_name}
          </span>
        );
      },
    },
    {
      id: 'client_id', accessorKey: 'client_id', header: 'ID', size: 80,
      meta: { mono: true, mobile: 'secondary' },
      cell: info => info.getValue(),
    },
    {
      id: 'authType', accessorKey: 'authType', header: 'Auth Type', size: 100,
      meta: { mobile: 'secondary' },
      cell: info => <span style={{ fontWeight: 500 }}>{info.getValue()}</span>,
    },
    {
      id: 'module', header: 'Module', size: 70, enableSorting: false,
      meta: { mobile: 'hidden' },
      cell: info => {
        const r = info.row.original;
        return pill(r.serviceLine === 'dme' ? 'DME' : 'HH', r.serviceLine === 'dme' ? 'var(--success)' : 'var(--accent2)', r.serviceLine === 'dme' ? 'var(--success-bg)' : 'var(--accent-bg)');
      },
    },
    {
      id: 'medicaid_id', accessorKey: 'medicaid_id', header: 'Medicaid ID', size: 130, enableSorting: false,
      meta: { mono: true, mobile: 'hidden' },
      cell: info => info.getValue() || '—',
    },
    {
      id: 'responsible', accessorKey: 'responsible_person_name', header: 'Responsible', size: 140,
      meta: { mobile: 'hidden' },
      cell: info => info.getValue() || '—',
    },
    {
      id: 'expiry', accessorKey: 'expiry', header: 'Expiry Date', size: 120,
      meta: { mobile: 'secondary' },
      cell: info => { const v = info.getValue(); return v ? format(parseLocalDate(v), 'MMM d, yyyy') : '—'; },
    },
    {
      id: 'status', accessorKey: 'current_status', header: 'Status', size: 170,
      meta: { truncate: true, mobile: 'hidden' },
      cell: info => info.getValue(),
    },
    {
      id: 'urgency', header: 'Urgency', size: 110,
      accessorFn: r => (r._urgency ? r._urgency.days : 99999),
      meta: { mobile: 'primary' },
      cell: info => {
        const u = info.row.original._urgency;
        return u
          ? <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 500, background: u.bg, color: u.color, whiteSpace: 'nowrap' }}>{u.label}</span>
          : <span style={{ color: 'var(--text3)', fontSize: 12 }}>—</span>;
      },
    },
    {
      id: 'actions', header: 'Actions', size: 180, enableSorting: false,
      meta: { mobile: 'secondary' },
      cell: info => {
        const r = info.row.original;
        return (
          <div style={{ display: 'flex', gap: 5 }} onClick={e => e.stopPropagation()}>
            <button className="btn btn-outline btn-sm" style={{ fontSize: 11 }} onClick={() => openAdd(r)}>Edit</button>
            {r.authType !== '—' && (
              <button className="btn btn-outline btn-sm" style={{ fontSize: 11 }} onClick={() => openHistory(r)}>History</button>
            )}
            {r.authType !== '—' && (
              <button
                className="btn btn-sm"
                style={{ fontSize: 11, background: r._isActionable ? 'var(--warn-bg)' : 'var(--bg2)', color: r._isActionable ? 'var(--warn)' : 'var(--text3)', border: `0.5px solid ${r._isActionable ? 'var(--warn-border)' : 'var(--border)'}`, cursor: 'pointer', fontFamily: 'inherit', borderRadius: 6, padding: '4px 8px' }}
                onClick={() => handleRenewal(r)}
              >
                ↻ Renew
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 80px)' }}>
      {fromClient && (
        <ClientTrail clientId={fromClient} sticky={false} crumbs={[{ label: 'Prior Auth' }]}
          right={<button type="button" className="btn btn-ghost btn-sm" onClick={() => { setSp({}); setSearch(''); }}>All clients</button>} />
      )}

      {success && <div style={{ background: 'var(--success-bg)', border: '0.5px solid var(--success-border)', borderRadius: 8, padding: '9px 14px', marginBottom: 10, fontSize: 12, color: 'var(--success)' }}>✓ {success}</div>}

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, marginBottom: 12, flexShrink: 0 }}>
        {[
          { label: 'Total Auth Records',  value: stats.total,    tone: 'neutral', onClick: () => setFilter('All') },
          { label: 'Expired',             value: stats.expired,  tone: stats.expired > 0 ? 'alert' : 'neutral', onClick: () => setFilter('Expired') },
          { label: 'Expiring < 60 days',  value: stats.expiring, tone: stats.expiring > 0 ? 'alert' : 'neutral', onClick: () => setFilter('Expiring ≤60d') },
          { label: 'No Auth on File',     value: stats.missing,  tone: 'neutral', onClick: () => setFilter('Missing') },
        ].map(s => (
          <StatTile key={s.label} label={s.label} value={s.value} tone={s.tone} onClick={s.onClick} />
        ))}
      </div>

      {/* Card */}
      <div className="card" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>

        {/* Header */}
        <div className="card-header" style={{ flexShrink: 0 }}>
          <div className="card-title">Authorization Tracker</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              placeholder="Search patient, ID, Medicaid..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ fontSize: 12, padding: '5px 10px', borderRadius: 8, border: '0.5px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', width: 200, fontFamily: 'inherit' }}
            />
            <button onClick={exportCSV} style={{ ...btnPill, background: 'transparent', borderColor: 'var(--border)', color: 'var(--text2)' }}>
              ↓ Export CSV
            </button>
            <button onClick={() => openAdd()} className="btn btn-primary btn-sm">+ Add Auth</button>
          </div>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 6, padding: '0 16px 10px', flexWrap: 'wrap', flexShrink: 0, alignItems: 'center' }}>
          {URGENCY_FILTERS.map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{ ...btnPill, background: filter === f ? 'var(--accent)' : 'transparent', color: filter === f ? '#fff' : 'var(--text3)', borderColor: filter === f ? 'var(--accent)' : 'var(--border)' }}>{f}</button>
          ))}
          <div style={{ width: '0.5px', height: 16, background: 'var(--border)', margin: '0 2px' }} />
          {TYPE_FILTERS.map(f => (
            <button key={f} onClick={() => setTypeFilter(f)} style={{ ...btnPill, background: typeFilter === f ? 'var(--bg3)' : 'transparent', color: typeFilter === f ? 'var(--text)' : 'var(--text3)', borderColor: typeFilter === f ? 'var(--border2)' : 'var(--border)' }}>{f}</button>
          ))}
          <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text3)' }}>{sorted.length} records</span>
        </div>

        {/* Table */}
        <div style={{ flex: 1, minHeight: 0 }}>
          <DataTable
            tableId="prior-auth"
            columns={columns}
            data={rows}
            getRowId={(r, i) => `${r.id}-${i}`}
            sorting={sorting}
            onSortingChange={handleSortingChange}
            manualSorting
            loading={loading}
            emptyMessage="No authorization records match this filter"
            maxHeight="calc(100vh - 300px)"
          />
        </div>
      </div>

      {/* ── Add / Edit Modal ── */}
      {showModal && (
        <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal" onMouseDown={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <div className="modal-header">
              <div className="modal-title">
                {editingRec ? `Edit Auth — ${editingRec.last_name}, ${editingRec.first_name}` : 'Add Authorization'}
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              {error && <div style={{ background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: 'var(--danger)' }}>{error}</div>}

              {!editingRec && (
                <div className="form-group">
                  <label className="form-label">Patient *</label>
                  <select className="form-select" value={form.clientId} onChange={e => setForm(p => ({ ...p, clientId: e.target.value }))}>
                    <option value="">Select patient...</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.last_name}, {c.first_name} — {c.client_id}</option>)}
                  </select>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Auth Type *</label>
                  <select className="form-select" value={form.authType} onChange={e => setForm(p => ({ ...p, authType: e.target.value }))}>
                    {AUTH_TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Expiry Date *</label>
                  <input type="date" className="form-input" value={form.expiryDate} onChange={e => setForm(p => ({ ...p, expiryDate: e.target.value }))} />
                </div>
              </div>

              {form.authType === 'PA' && (
                <>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">PA / Auth Number</label>
                      <input className="form-input" value={form.authNumber} onChange={e => setForm(p => ({ ...p, authNumber: e.target.value }))} placeholder="e.g. DMA80-2024-001" />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Authorized Hr/Week — Skilled</label>
                      <input type="number" step="0.5" min="0" className="form-input" value={form.skilledHoursWeek} onChange={e => setForm(p => ({ ...p, skilledHoursWeek: e.target.value }))} placeholder="e.g. 40" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Authorized Hr/Week — PSS</label>
                      <input type="number" step="0.5" min="0" className="form-input" value={form.pssHoursWeek} onChange={e => setForm(p => ({ ...p, pssHoursWeek: e.target.value }))} placeholder="e.g. 25" />
                    </div>
                  </div>
                </>
              )}

              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-textarea" rows={2} value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Optional notes about this authorization..." />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : editingRec ? 'Save Changes' : 'Add Authorization'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── History Modal ── */}
      {historyRec && (
        <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && setHistoryRec(null)}>
          <div className="modal" style={{ maxWidth: 720 }}>
            <div className="modal-header">
              <div>
                <div className="modal-title">{historyRec.authType} History</div>
                <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
                  {historyRec.last_name}, {historyRec.first_name} · {historyRec.client_id}
                </div>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => setHistoryRec(null)}>✕</button>
            </div>
            <div className="modal-body">
              <AuthHistoryTable rows={historyRows} />
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setHistoryRec(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
